import type { HTTPError } from 'ky'

import type { IIdeaStatus } from '~/entities/content/idea/interfaces/idea'
import type { IUser } from '~/entities/user/user/interfaces/user'

import type { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { ID, IEntity } from '~/features-shared/libs/interfaces/entity'
import type { IMemberRole } from '~/features-shared/member/interfaces/search'
import type { ITag } from '~/features-shared/tags/interfaces/tags'

import type { SwymHTTPError } from '~/typings/SwymHTTPError'

export type COMMUNITY_ID = ID

/** Community type, can be 'community' or 'dm' or 'personalspace */
export type CommunityType = 'community' | 'dm' | 'personalspace' | 'externaldm'

export enum ECommunityExtranetAccessEnabled {
  Disabled = 0,
  Activated = 1,
}

/** WhatsNew visibility. '0': visible | '1': hidden. */
export enum ECommunityBlacklistByDefault {
  Disabled = '0',
  Activated = '1',
}

/**
 * Access level of the community (public, private or secret).
 * @info Private = 0
 * @info Public = 1
 * @info Secret = 4
 */
export enum ECommunityAccess {
  Private = 0,
  Public = 1,
  Secret = 4,
}

/**
 * User community role.
 * @info SUPERUSER = 4
 * @info OWNER = 3
 * @info AUTHOR = 2
 * @info CONTRIBUTOR = 1
 */
export enum ECommunityRole {
  SUPERUSER = 4,
  OWNER = 3,
  AUTHOR = 2,
  CONTRIBUTOR = 1,
}

/**
 * Community creation/join request status
 * @info ACCEPTED =  '1'   - Accepted by admin
 * @info SENT = '2'        - User sent request and waiting admin response
 * @info PENDING = '3'     - Admin send back review request and user need to resend request
 * @info DECLINED = '0'    - Declined by admin
 * @info TRANSFORMED = '4'    - Status accepted for request transformed from conversation
 */
export enum ECommunityRequestStatus {
  ACCEPTED = '1',
  SENT = '2',
  PENDING = '3',
  DECLINED = '0',
  TRANSFORMED = '4',
}

export enum ED6SWCommunityRole {
  SUPERUSER = 'ds6w:role.swym_mainowner',
  OWNER = 'ds6w:role.swym_owner',
  AUTHOR = 'ds6w:role.swym_author',
  CONTRIBUTOR = 'ds6w:role.swym_contributor',
}

export function toCommunityRole(role: ECommunityRole): IMemberRole {
  const roles: Record<ECommunityRole, IMemberRole> = {
    [ECommunityRole.OWNER]: 'Owner',
    [ECommunityRole.AUTHOR]: 'Author',
    [ECommunityRole.CONTRIBUTOR]: 'Contributor',
    [ECommunityRole.SUPERUSER]: 'Main Owner',
  }
  return roles[role]
}

export function toECommunityRole(role: IMemberRole): ECommunityRole {
  const roles: Record<IMemberRole, ECommunityRole> = {
    'Owner': ECommunityRole.OWNER,
    'Author': ECommunityRole.AUTHOR,
    'Contributor': ECommunityRole.CONTRIBUTOR,
    'Main Owner': ECommunityRole.SUPERUSER,
  }
  return roles[role]
}

export function toCommunityRoleFromD6SW(role: ED6SWCommunityRole): string {
  const roles = {
    [ED6SWCommunityRole.OWNER]: 'Owner',
    [ED6SWCommunityRole.AUTHOR]: 'Author',
    [ED6SWCommunityRole.CONTRIBUTOR]: 'Contributor',
    [ED6SWCommunityRole.SUPERUSER]: 'Main Owner',

  }
  return roles[role]
}

export function fromCommunityRoletoD6SW(role: ECommunityRole): string {
  const roles = {
    [ECommunityRole.OWNER]: ED6SWCommunityRole.OWNER,
    [ECommunityRole.AUTHOR]: ED6SWCommunityRole.AUTHOR,
    [ECommunityRole.CONTRIBUTOR]: ED6SWCommunityRole.CONTRIBUTOR,
    [ECommunityRole.SUPERUSER]: ED6SWCommunityRole.SUPERUSER,
  }
  return roles[role]
}

export interface ICommunityContentTypeMinACL {
/** Conference minimum required rights to create */
  conference_min_acl: ECommunityRole
  /** Idea minimum required rights to create */
  ideation_min_acl: ECommunityRole
  /** Media minimum required rights to create */
  media_min_acl: ECommunityRole
  /** Post minimum required rights to create */
  post_min_acl: ECommunityRole
  /** Question minimum required rights to create */
  qna_min_acl: ECommunityRole
  /** Survey minimum required rights to create */
  survey_min_acl: ECommunityRole
  /** Wedo minimum required rights to create */
  wedo_min_acl: ECommunityRole
  /** Wiki minimum required rights to create */
  wiki_min_acl: ECommunityRole
}

export interface ICommunityContentTypeEnabled {
/** Conference is enabled */
  conference_is_enabled: boolean
  /** Idea is enabled */
  ideation_is_enabled: boolean
  /** Media is enabled */
  media_is_enabled: boolean
  /** Post is enabled */
  post_is_enabled: boolean
  /** Question is enabled */
  qna_is_enabled: boolean
  /** Survey is enabled */
  survey_is_enabled: boolean
  /** Wedo is enabled */
  wedo_is_enabled: boolean
  /** Wiki is enabled */
  wiki_is_enabled: boolean
}

export interface ICommunity extends IEntity, ICommunityContentTypeMinACL, ICommunityContentTypeEnabled {
  /** Access level of the community (public, private or secret) */
  access: ECommunityAccess
  /** Is blacklisted by default ? Equivalent to is_  */
  blacklist_by_default: '0' | '1'
  /** Whether the community is a Business Experience Community */
  community_be: boolean
  /** Community type, can be 'community' or 'dm' or 'personalspace */
  community_type: CommunityType | undefined

  /** vBrick for Conference is enabled */
  conference_vbrick_enabled: boolean
  /**
   * Default type use for creation in community whatsnew.
   * @default IContentModel.Post
   */
  default_type_for_creation: IContentModel
  /** Description of the community */
  description: string
  /** External user access right */
  extranet_access: ECommunityRole
  /** URI of the highlighted content */
  highlighted_content_uri?: string

  /** Disable instant message */
  instant_message_is_enabled: boolean
  /** Hidden if the user choose to unsubscribe */
  is_hidden: boolean
  /** Last received message id in the timeline */
  last_message?: { message_id: string }
  /** Last read message id in the timeline */
  mark?: { message_id: string }

  /** Disable members edition */
  members_edition_ui_blocked: boolean
  /** Owner of the community */
  owner: IUser

  /** Thumbnail fingerprint. */
  preview_fingerprint?: string

  /** Disable the community settings */
  settings_edition_ui_blocked: boolean
  /** Stats minimum required rights to create - give access to statistics page */
  stats_min_acl: ECommunityRole

  /** Explicit tags of a community */
  tags6w: ITag[]
  /** Implicit tags of a community */
  tags6w_implicit: ITag[]
  /** Make mandatory tags on content */
  tags_on_content_creation_required: boolean
  /** Is the user external? 0 = false, 1 = true */
  tenant_user_status: number
  /** Thumbnail ID of the community */
  thumbnail?: string
  /** Community title */
  title: string
  /** User access right */
  user_access: ECommunityRole

  /** Has Terms of Use to sign */
  tou_to_sign: boolean
  /** Transfer Idea to App activation */
  transfer_idea_to_app: boolean
  /** Mention all activation */
  mention_all_min_acl: `${ECommunityRole}`
  /** Ideation status list */
  ideation_status: IIdeaStatus[]
  /** Tenant admin */
  tenant_admin: string
  /** If enable External user access right */
  extranet_access_enabled: ECommunityExtranetAccessEnabled
  /** True is the user has access to this community */
  hasUserAccess: boolean
  /** Position of the community in section */
  position: number
}

export interface NonMemberPrivateCommunityAccessError extends HTTPError {
  data?: {
    result: INonMemberPrivateCommunity
  }
}

export type INonMemberPrivateCommunity = Pick<
  ICommunity,
  'id' |
  'subject_uri' |
  'access' |
  'title' |
  'description' |
  'extranet_access' |
  'hasUserAccess' |
  'owner' |
  'preview_fingerprint' |
  'tou_to_sign' |
  'community_type'
>

export interface ICommunityRequest extends ICommunity {
  community_id: string
  creator_user_id: string
  description: string
  domain_id: string
  go_live_date: string
  lifespan: string
  primary_community_owner_user_hash_key: string
  status: ECommunityRequestStatus
  type_id: string
  benefits: string
  /** Additional comment left by the administrator */
  additional_comment?: string
  /** Community creation update date */
  update: string
  user_creator_request: IUser
  primary_community_owner: IUser
  /** Base community uri if community accepted, for user can redirect */
  base_community_uri?: string
}

export interface ITenantMarks {
  context: 'communities' | 'favorite_communities'
  message_id: string
}

export interface ICommunityLiteResponse {
  communities: ICommunity[]
}

export interface ICommunityLiteMarksResponse {
  communities: Pick<ICommunity, 'id' | 'subject_uri' | 'mark' | 'last_message'>[]
}

export interface CreateCommunityData {
  /**
   * Employee (internal) users max access right to the community.
   * @see ECommunityAccess (Stringified value of)
   */
  access: string

  /**
   * WhatsNew section visibility.
   * @see ECommunityBlacklistByDefault
   */
  blacklist_by_default: '0' | '1'

  /** Community description */
  description: string

  /**
   * External users max access right to the community.
   * @see ECommunityAccess (Stringified value of)
   */
  extranet_access: string

  /** Allow external users to access to the community. */
  extranet_access_enabled: '0' | '1'

  /** Community main owner user's hash_key */
  hash_key: string

  /** Community title */
  title: string
}

interface CommunityError extends SwymHTTPError {
  errorcode?: 'ERROR_COMMUNITY_CREATION_TITLE_UNIQUENESS' | 'IllegalArgumentException' | 'SocialServiceException'
}
export type CreateCommunityError = CommunityError
export type EditCommunityError = CommunityError

export interface CreateCommunityRequestData {
  /** Community benefits input description */
  benefits: string

  /**
   * Enable or disable WhatsNew section.
   * @see ECommunityBlacklistByDefault
   */
  blacklist_by_default: '0' | '1'

  /** Community description */
  description: string

  /**
   * External users max access right to the community.
   * @see ECommunityAccess (Stringified value of)
   */
  extranet_access: string

  /** Allow external users to access to the community. */
  extranet_access_enabled: '0' | '1'

  /**
   * Employee (internal) users max access right to the community.
   * @see ECommunityAccess (Stringified value of)
   */
  intranet_access: string

  /** Community main owner user's hash_key */
  primary_community_owner_hash_key: string

  /** Community title */
  title: string

  /** dm to be transformed into community */
  community_dm_id?: string
}

export interface IUnfollowedCommunity {
  id: string
  model: string
}

/**
 * Defines various pages within community like settings page, members page ..etc
 */
export type CommunityPage = 'members' | 'settings' | 'statistics' | 'recycle-bin'

/**
 * Defines data extracted from old swym-V2 url hash string.
 */
export interface CommunityRoute {
  /** Id of the community */
  communitySubjectUri: string
  /** Community type 'dm' or 'community' */
  communityType: CommunityType
  /** Content Id - Unique identifier */
  contentId?: string
  /** Type of the content */
  contentType?: IContentModel
  /** Derived type ID(If any) */
  derivedTypeId?: string
  /** Message ID in DM */
  messageId?: string
  /** Comment Id of the content */
  comment?: string
  /** Answer ID  of the content */
  answer?: string
  /** Reply Id  of the content */
  reply?: string
  /** Page name in the community or in DM */
  page?: CommunityPage
}

export interface GetCommunityRequestData {
  page: string
  limit: string
}

export interface UpdateCommunityRequestData {
  title: string
  benefits: string
  description: string
  go_live_date: string
  id: string
  /** Internal user access right in community  */
  intranet_access?: ECommunityAccess
  /** External user access right */
  extranet_access: ECommunityAccess
  /** Community main owner user's hash_key */
  primary_community_owner_hash_key: string
  lifespan: string
  /** If enable External user access right */
  extranet_access_enabled: ECommunityExtranetAccessEnabled
  /** Deactivate What's new */
  blacklist_by_default: '0' | '1'
}

export interface TermsOfUseInfos {
  filename?: string
  message?: string
}

export interface HighlightContent {
  community_id: string
  highlighted_content_uri: string
}

/** The response of community type in FedSearch */
export enum CommunityTypeFedSearch {
  DM = 'swym:DirectMessage',
  COMMUNITY = 'swym:Community',
  PERSONALSPACE = 'swym:PersonalSpace',
}

export interface mentionAllAvailabilityMinRoleOptionsInfos {
  label: string
  value: ECommunityRole
}
